import * as Cookies from 'js-cookie';
import * as React from 'react';

import * as api from '../../api';
import * as analytics from '../../api/analytics';
import ProgressBanner from '../../components/sellerReturnExperience/ProgressBanner';
import * as globals from '../../helpers/globals';

interface IState {
  addressString: string;
  leadState: string;
  leadToken: string;
  loaded: boolean;
  offerState: string;
}

export const ACTIVE_DASHBOARD_OFFER_STATES = ['accepted', 'in_contract', 'contingency_released'];
export const READY_OFFER_STATES = ['sent', 'viewing'];

export const INPUT_FLOW_LEAD_STATES = ['inputting_answers'];
export const PENDING_LEAD_STATES = ['needs_attention', 'pending'];

export default class SellerReturnExperience extends React.Component<{}, IState> {
  public state: IState = {
    addressString: undefined,
    leadState: undefined,
    leadToken: undefined,
    loaded: false,
    offerState: undefined,
  };

  public componentDidMount() {
    this.fetchSellerReturnExperience();
  }

  public render() {
    return this.state.loaded ? this.renderBanner() : null;
  }

  private renderBanner() {
    const { offerState, leadState, leadToken } = this.state;
    let shouldDisplay = false;
    let progress: string;
    let ctaText: string;
    let returnExperienceType: string;
    let shouldDisplayCta: boolean;
    let handleCtaClick: () => void;
    let renderBannerFn: () => void;

    if (INPUT_FLOW_LEAD_STATES.indexOf(leadState) !== -1) {
      progress = 'progress-quarter';
      shouldDisplay = true;
      shouldDisplayCta = true;
      renderBannerFn = this.renderBannerFnInputFlow;
      ctaText = 'Continue';
      returnExperienceType = 'input_flow';
      handleCtaClick = this.fnHandleCTA(
        `${globals.CONSUMER_FE_URL}/seller-lead/${leadToken}`,
        returnExperienceType,
        leadToken,
      );
    } else if (PENDING_LEAD_STATES.indexOf(leadState) !== -1) {
      progress = 'progress-three-quarters';
      shouldDisplay = true;
      shouldDisplayCta = true;
      renderBannerFn = this.renderBannerFnOfferPending;
      ctaText = 'Check status';
      returnExperienceType = 'offer_pending';
      handleCtaClick = this.fnHandleCTA(
        `${globals.CONSUMER_FE_URL}/seller-lead/${leadToken}/p/complete`,
        returnExperienceType,
        leadToken,
      );
    } else if (READY_OFFER_STATES.indexOf(offerState) !== -1) {
      shouldDisplay = true;
      shouldDisplayCta = false;
      renderBannerFn = this.renderBannerFnOfferReady;
      returnExperienceType = 'offer_ready';
    } else if (ACTIVE_DASHBOARD_OFFER_STATES.indexOf(offerState) !== -1) {
      shouldDisplay = true;
      shouldDisplayCta = true;
      renderBannerFn = this.renderBannerFnActiveAcceptedOffer;
      ctaText = 'Go to dashboard';
      returnExperienceType = 'active_offer_accepted';
      handleCtaClick = this.fnHandleCTA(
        `${location.origin}/offers/${leadToken}/dashboard`,
        returnExperienceType,
        leadToken,
      );
    }
    if (shouldDisplay) {
      analytics.trackEvent('seller-return-experience', 'view', 'success', {
        lead_token: leadToken,
        return_experience_type: returnExperienceType,
      });

      return (
        <ProgressBanner
          progress={progress}
          ctaText={ctaText}
          shouldDisplayCta={shouldDisplayCta}
          onCtaClick={handleCtaClick}
          renderBannerFn={renderBannerFn}
        />
      );
    }
    return null;
  }

  private fnHandleCTA = (url: string, returnExperienceType: string, leadToken: string) => {
    return () => {
      analytics.trackEvent('seller-return-experience', 'click', 'success', {
        lead_token: leadToken,
        return_experience_type: returnExperienceType,
      });
      window.location.href = url;
    };
  };

  private renderBannerFnInputFlow = () => {
    const { addressString } = this.state;
    return (
      <div>
        <strong> Welcome back! </strong>
        Take a minute to complete your offer on <strong>{addressString}</strong>
      </div>
    );
  };

  private renderBannerFnOfferPending = () => {
    const { addressString } = this.state;
    return (
      <div>
        <strong> Get excited! </strong>
        Your offer for <strong>{addressString}</strong> is almost ready
      </div>
    );
  };

  private renderBannerFnOfferReady = () => {
    const { addressString } = this.state;
    return (
      <div>
        <strong> Welcome back! </strong>
        Your offer for <strong>{addressString}</strong> is ready. Check your email!
      </div>
    );
  };

  private renderBannerFnActiveAcceptedOffer = () => {
    const { addressString } = this.state;
    return (
      <div>
        <strong> Welcome Back! </strong>
        Check in on your sale of <strong>{addressString}</strong>.
      </div>
    );
  };

  private async fetchSellerReturnExperience() {
    const leadToken = Cookies.get('lead_token');
    if (!leadToken) {
      return;
    }
    const data = await api.fetchSellerReturnExperience(leadToken);
    if (!data) {
      return;
    }
    this.setState({ ...this.getStateFromData(data, leadToken) });
  }

  private getStateFromData(data, leadToken) {
    if (!data) {
      return;
    }

    let leadState;
    let offerState;
    let address;

    // This condition is to appease feature tests that mock action.payload to be a non-BFF response
    if (data.value === undefined) {
      leadState = data.lead_state;
      offerState = data.offer_state;
      address = data.address;
    } else {
      // data is a BFF response
      address = data.value.address;
      leadState = data.value.leadState;
      offerState = data.value.offerState;
    }

    if (!address || !address.street || !address.city) {
      return;
    }

    return {
      addressString: `${address.street}, ${address.city}`,
      leadState,
      leadToken,
      loaded: true,
      offerState,
    };
  }
}
